/**
 * FDV — carrito lateral: `fdv_cart_drawer_opened` y `fdv_cart_drawer_closed`.
 *
 * Qué ve la persona al abrirse el cajón (piezas, subtotal, cuánto falta para el envío gratis y
 * para el siguiente regalo) y qué hace después. El tema emite `shopify:cart:view` con
 * `context: 'dialog'` en cada apertura (verificado en desarrollo), pero en Events solo hay unos
 * cientos de `cart_viewed` al mes, así que no se depende de que el píxel lo reenvíe.
 *
 * Datos del motor de regalos (`FDV.gifts`, en centavos): se leen cuando el motor publica su
 * estado (`fdv:gifts:state`) o, si no lo hace, tras 1.5 s al agregar una pieza y 0.3 s en los demás casos
 * (a 400 ms fijos salía desactualizado tras agregar: verificado en desarrollo). `origen` es aproximado:
 * `auto_al_agregar` si hubo un cambio de líneas en los 1.5 s anteriores, `icono` si hubo un clic
 * en el ícono del carrito, `otro` en los demás casos (p. ej. restaurar la página con el cajón abierto).
 * `siguiente` al cerrar: `checkout` si hubo clic en «Finalizar compra», si no `cerrar`
 * (no se distingue «seguir comprando»).
 *
 * Especificación: flor_venus_comercial_intelligence,
 * 2026/10-octubre/campanas/2026-10-07_alcance-y-captacion/eventos-nuevos.md (§3).
 */
(() => {
  const FDV = window.FDV;
  if (!FDV || typeof FDV.track !== 'function' || FDV.cartDrawer) return;

  const V = 1;
  const ORIGEN_MS = 1500;
  const SETTLE_AUTO_MS = 1500; // tras agregar, el motor tarda en reconciliar el carrito
  const SETTLE_OTHER_MS = 300;
  FDV.cartDrawer = { v: V };

  let lastLinesUpdate = -Infinity;
  let lastIconClick = -Infinity;
  let openedAt = null;
  let wentCheckout = false;

  const now = () => performance.now();
  const pesos = (centavos) => (typeof centavos === 'number' ? Math.round(centavos / 100) : null);

  function send(name, props) {
    if (FDV.consent?.analytics === false) return;
    try {
      FDV.track(name, props);
    } catch (e) {
      /* el tracking nunca debe romper la página */
    }
  }

  function snapshot() {
    const cart = FDV.gifts?.cart;
    const state = Array.isArray(FDV.gifts?.state) ? FDV.gifts.state : [];
    const envio = state.find((s) => typeof s.id === 'string' && s.id.startsWith('envio-gratis'));
    const regalos = state.filter((s) => s.mechanism === 'zero_price' && s.kind === 'threshold');
    const desbloqueados = regalos.filter((s) => s.status === 'unlocked');
    const siguiente = regalos
      .filter((s) => s.status !== 'unlocked' && typeof s.remainingAmount === 'number' && s.remainingAmount > 0)
      .sort((a, b) => a.remainingAmount - b.remainingAmount)[0];
    return {
      items: typeof cart?.item_count === 'number' ? cart.item_count : null,
      subtotal: pesos(cart?.items_subtotal_price),
      falta_envio_gratis: envio ? pesos(envio.status === 'unlocked' ? 0 : envio.remainingAmount) : null,
      regalo_desbloqueado: desbloqueados.length ? desbloqueados[desbloqueados.length - 1].id : null,
      falta_siguiente_regalo: siguiente ? pesos(siguiente.remainingAmount) : null,
    };
  }

  document.addEventListener('shopify:cart:lines-update', () => {
    lastLinesUpdate = now();
  });

  document.addEventListener(
    'click',
    (e) => {
      const t = e.target instanceof Element ? e.target : null;
      if (!t) return;
      if (t.closest('cart-icon')) lastIconClick = now();
      if (openedAt != null && t.closest('#checkout')) wentCheckout = true;
    },
    true
  );

  document.addEventListener('shopify:cart:view', (e) => {
    if (e.context !== 'dialog' || openedAt != null) return;
    const t = now();
    // Prueba `carrito_v1` (fdv-exp-carrito.js): la barra y los hitos dejan una pista de quién abrió el cajón.
    const pista = FDV.cartDrawerOrigenPista;
    const origen =
      pista && t - pista.t < ORIGEN_MS
        ? pista.origen
        : t - lastLinesUpdate < ORIGEN_MS
          ? 'auto_al_agregar'
          : t - lastIconClick < ORIGEN_MS
            ? 'icono'
            : 'otro';
    openedAt = t;
    wentCheckout = false;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      document.removeEventListener('fdv:gifts:state', onState);
      send('fdv_cart_drawer_opened', { ...snapshot(), origen, v: V });
    };
    const onState = () => setTimeout(finish, 50);
    if (origen === 'auto_al_agregar') document.addEventListener('fdv:gifts:state', onState);
    setTimeout(finish, origen === 'auto_al_agregar' ? SETTLE_AUTO_MS : SETTLE_OTHER_MS);
  });

  document.addEventListener('theme-drawer:close', (e) => {
    if (openedAt == null) return;
    // Otros cajones (menú, cuenta) también avisan: solo el del carrito.
    if (e.target instanceof Element && !e.target.querySelector('cart-drawer-component') && !e.target.closest('cart-drawer-component')) return;
    const seconds = Math.round((now() - openedAt) / 1000);
    openedAt = null;
    send('fdv_cart_drawer_closed', { segundos_abierto: seconds, siguiente: wentCheckout ? 'checkout' : 'cerrar', ...snapshot(), v: V });
  });
})();
